import { useState, useEffect } from 'react';
import { Modal, Button, Input, Select } from '../common';
import { ROLE_META } from '../../utils/constants';
import { updateUser } from '../../services/userService';

/**
 * Edit User Modal
 * Admin can update user details including commission_per_deal
 */
export function EditUserModal({ isOpen, onClose, showToast, user, onUserUpdated }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "marketer",
    commission_per_deal: null,
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: user.role || "marketer",
        commission_per_deal: user.commission_per_deal || null,
      });
    }
  }, [user]);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      showToast("يرجى ملء جميع الحقول المطلوبة", "error");
      return;
    }

    setSubmitting(true);
    try {
      const updatedUser = await updateUser(user.id, formData);
      
      if (updatedUser) {
        showToast("✅ تم تحديث الحساب بنجاح");
        
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
        
        onClose();
      }
    } catch (err) {
      console.error('Error updating user:', err);
      showToast(err.message || '❌ فشل في تحديث الحساب', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Build role options (exclude admin)
  const roleOptions = Object.entries(ROLE_META)
    .filter(([key]) => key !== 'admin')
    .map(([key, meta]) => ({
      value: key,
      label: meta.label, // Only Arabic label, no icon name
    }));

  return (
    <Modal 
      title="✏️ تعديل المستخدم" 
      isOpen={isOpen} 
      onClose={onClose}
      size="md"
    >
      <div style={{ direction: "rtl" }}>
        <Input
          label="الاسم الكامل"
          value={formData.name}
          onChange={e => updateField('name', e.target.value)}
          placeholder="سارة أحمد"
          required
        />

        <Input
          label="البريد الإلكتروني"
          type="email"
          value={formData.email}
          onChange={e => updateField('email', e.target.value)}
          placeholder="sara@gosnel.com"
          required
        />

        <Input
          label="رقم الهاتف (اختياري)"
          type="tel"
          value={formData.phone}
          onChange={e => updateField('phone', e.target.value)}
          placeholder="+971 50 123 4567"
        />

        <Select
          label="الدور"
          value={formData.role}
          onChange={e => updateField('role', e.target.value)}
          options={roleOptions}
        />

        {(formData.role === 'marketer' || formData.role === 'rep') && (
          <Input
            label="العمولة لكل صفقة ناجحة (AED) - اختياري"
            type="number"
            value={formData.commission_per_deal || ''}
            onChange={e => updateField('commission_per_deal', parseFloat(e.target.value) || null)}
            placeholder={formData.role === 'marketer' ? '20' : '35'}
          />
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            {submitting ? "جاري التحديث..." : "تحديث الحساب"}
          </Button>
          <Button 
            variant="secondary" 
            onClick={onClose}
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
}
